import * as React from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
    FormControlLabel, Radio, RadioGroup, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { Link as RouterLink } from 'react-router-dom';
import { importAiBundle } from '../../Api';
import {
    AI_BUNDLE_FORMAT, AiBundle, AiImportAction, AiImportItemDto, AiImportMode, AiImportReportDto,
} from '../../models/dtos/AiDto';
import { ConfirmDialog } from '../betupFeed/BetupFeedShared';
import { AiActionError, AiCodeEditor, AiErrorAlert, formatAiDate, textError, toAiActionError } from './AiShared';
import { describeAiBundle } from './AiBundleExport';

type ChipColor = 'default' | 'success' | 'info' | 'error';

const ACTION_COLOR: Record<AiImportAction, ChipColor> = {
    CREATE: 'success', UPDATE: 'info', UNCHANGED: 'default', SKIP: 'default', ERROR: 'error',
};

export function AiImportActionChip({ action }: { action: AiImportAction | string }) {
    const color = ACTION_COLOR[action as AiImportAction] ?? 'default';
    return <Chip size="small" color={color} variant={action === 'SKIP' ? 'outlined' : 'filled'} label={action} />;
}

interface ParsedBundle {
    bundle: AiBundle | null;
    /** Shown under the editor; null for empty text. */
    error: string | null;
}

/** Parses + checks the client-side bits of the contract; the server validates the rest. */
function parseBundle(text: string): ParsedBundle {
    if (!text.trim()) return { bundle: null, error: null };
    let value: unknown;
    try {
        value = JSON.parse(text);
    } catch (e) {
        return { bundle: null, error: `Invalid JSON: ${e instanceof Error ? e.message : String(e)}` };
    }
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        return { bundle: null, error: 'Invalid bundle: expected a JSON object.' };
    }
    const obj = value as Record<string, unknown>;
    if (obj.format !== AI_BUNDLE_FORMAT) {
        return { bundle: null, error: `Not a BETUP AI bundle: "format" must be "${AI_BUNDLE_FORMAT}".` };
    }
    if (obj.tasks !== undefined && obj.tasks !== null && !Array.isArray(obj.tasks)) {
        return { bundle: null, error: 'Invalid bundle: "tasks" must be an array.' };
    }
    if (obj.agents !== undefined && obj.agents !== null && !Array.isArray(obj.agents)) {
        return { bundle: null, error: 'Invalid bundle: "agents" must be an array.' };
    }
    return { bundle: value as AiBundle, error: null };
}

function ImportSummary({ report }: { report: AiImportReportDto }) {
    const s = report.summary;
    return (
        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
            <Chip size="small" color="success" variant={s.created > 0 ? 'filled' : 'outlined'} label={`${s.created} created`} />
            <Chip size="small" color="info" variant={s.updated > 0 ? 'filled' : 'outlined'} label={`${s.updated} updated`} />
            <Chip size="small" variant="outlined" label={`${s.unchanged} unchanged`} />
            <Chip size="small" variant="outlined" label={`${s.skipped} skipped`} />
            <Chip size="small" color="error" variant={s.errors > 0 ? 'filled' : 'outlined'} label={`${s.errors} errors`} />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {report.mode}{report.dryRun ? ' · dry run' : ''}{report.applied ? ' · applied' : ' · nothing written'}
            </Typography>
        </Stack>
    );
}

function itemPath(item: AiImportItemDto): string {
    return `/ai/${item.type === 'AGENT' ? 'agents' : 'tasks'}/${encodeURIComponent(item.code)}`;
}

function ImportReportTable({ report, linkItems, onNavigate }: {
    report: AiImportReportDto;
    /** Codes become links (after a successful import). */
    linkItems: boolean;
    onNavigate: () => void;
}) {
    if (report.items.length === 0) return <Typography variant="body2" color="text.secondary">The bundle has no items.</Typography>;
    return (
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 360 }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: 90 }}>Type</TableCell>
                        <TableCell>Code</TableCell>
                        <TableCell sx={{ width: 120 }}>Action</TableCell>
                        <TableCell>Changed fields</TableCell>
                        <TableCell>Messages</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {report.items.map(item => (
                        <TableRow key={`${item.type}:${item.code}`} sx={item.action === 'ERROR' ? { bgcolor: 'rgba(211, 47, 47, 0.06)' } : undefined}>
                            <TableCell><Typography variant="caption" fontWeight={600}>{item.type}</Typography></TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                                {linkItems && item.action !== 'ERROR'
                                    ? <RouterLink to={itemPath(item)} onClick={onNavigate}>{item.code}</RouterLink>
                                    : item.code}
                            </TableCell>
                            <TableCell><AiImportActionChip action={item.action} /></TableCell>
                            <TableCell>
                                <Stack direction="row" gap={0.5} flexWrap="wrap">
                                    {(item.changedFields ?? []).map(f => (
                                        <Chip key={f} size="small" variant="outlined" label={f} sx={{ fontFamily: 'monospace', fontSize: 11 }} />
                                    ))}
                                    {(item.changedFields ?? []).length === 0 && <Typography variant="caption" color="text.secondary">—</Typography>}
                                </Stack>
                            </TableCell>
                            <TableCell>
                                {(item.messages ?? []).length === 0
                                    ? <Typography variant="caption" color="text.secondary">—</Typography>
                                    : (item.messages ?? []).map((m, i) => (
                                        <Typography key={i} variant="body2" color={item.action === 'ERROR' ? 'error' : undefined}
                                            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                            {m}
                                        </Typography>
                                    ))}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

/**
 * Import of a `betup-ai-bundle` file: pick / paste → choose mode → dry-run preview → import (all-or-nothing).
 * Import is enabled only after an error-free preview of exactly the current text and mode.
 */
export default function AiImportDialog({ open, onClose, onImported }: {
    open: boolean;
    onClose: () => void;
    /** Called after a successful (applied) import, e.g. to reload the list. */
    onImported?: (report: AiImportReportDto) => void;
}) {
    const [text, setText] = React.useState('');
    const [fileName, setFileName] = React.useState<string | null>(null);
    const [mode, setMode] = React.useState<AiImportMode>('UPSERT');
    const [busy, setBusy] = React.useState<'preview' | 'import' | null>(null);
    const [error, setError] = React.useState<AiActionError | null>(null);
    const [preview, setPreview] = React.useState<AiImportReportDto | null>(null);
    const [previewKey, setPreviewKey] = React.useState<string | null>(null);
    const [result, setResult] = React.useState<AiImportReportDto | null>(null);
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const fileInput = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        if (!open) return;
        setText('');
        setFileName(null);
        setMode('UPSERT');
        setBusy(null);
        setError(null);
        setPreview(null);
        setPreviewKey(null);
        setResult(null);
        setConfirmOpen(false);
    }, [open]);

    const parsed = React.useMemo(() => parseBundle(text), [text]);
    const currentKey = `${mode}\n${text}`;
    const previewCurrent = preview != null && previewKey === currentKey;
    const applied = result?.applied === true;
    const changes = preview ? preview.summary.created + preview.summary.updated : 0;
    const canImport = previewCurrent && preview != null && preview.summary.errors === 0 && changes > 0 && !applied && busy == null;

    const onFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        try {
            const content = await file.text();
            setText(content);
            setFileName(file.name);
            setError(null);
            setResult(null);
        } catch (e) {
            setError(textError(`Could not read ${file.name}: ${e instanceof Error ? e.message : String(e)}`));
        }
    };

    const runPreview = async () => {
        if (parsed.bundle == null) return;
        const key = currentKey;
        setBusy('preview');
        setError(null);
        setResult(null);
        const response = await importAiBundle(parsed.bundle, { mode, dryRun: true });
        setBusy(null);
        if (response.error != null || response.data == null) {
            setError(toAiActionError(response, 'Preview failed') ?? textError('Preview failed: empty response'));
            setPreview(null);
            setPreviewKey(null);
            return;
        }
        setPreview(response.data);
        setPreviewKey(key);
    };

    const runImport = async () => {
        setConfirmOpen(false);
        if (parsed.bundle == null || !canImport) return;
        setBusy('import');
        setError(null);
        const response = await importAiBundle(parsed.bundle, { mode, dryRun: false });
        setBusy(null);
        if (response.error != null || response.data == null) {
            setError(toAiActionError(response, 'Import failed') ?? textError('Import failed: empty response'));
            return;
        }
        setResult(response.data);
        if (response.data.applied) {
            onImported?.(response.data);
        } else {
            // The database changed since the preview: require a fresh one.
            setPreview(null);
            setPreviewKey(null);
        }
    };

    const report = result ?? (previewCurrent ? preview : null);
    const stalePreview = preview != null && !previewCurrent && result == null;
    const importedLinks = applied && result ? result.items.filter(i => i.action === 'CREATE' || i.action === 'UPDATE') : [];
    const bundle = parsed.bundle;

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="lg" fullWidth>
            <DialogTitle>Import AI tasks and agents</DialogTitle>
            <DialogContent dividers>
                <Stack gap={2}>
                    <Stack gap={1}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                            <Typography variant="subtitle1" fontWeight={600}>1. Bundle</Typography>
                            <Stack direction="row" alignItems="center" gap={1}>
                                {fileName && <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{fileName}</Typography>}
                                <input ref={fileInput} type="file" accept=".json,application/json" hidden onChange={onFile} />
                                <Button variant="outlined" size="small" startIcon={<UploadFileIcon />} disabled={busy != null || applied}
                                    onClick={() => fileInput.current?.click()}>
                                    Choose file…
                                </Button>
                            </Stack>
                        </Stack>
                        <AiCodeEditor language="json" path="ai-import-bundle.json" value={text} height={300} readOnly={busy != null || applied}
                            onChange={next => { setText(next); setResult(null); }}
                            helper={`Choose an exported .json file or paste a bundle here ("format": "${AI_BUNDLE_FORMAT}"). You can review and edit it before importing.`} />
                        {parsed.error && <Alert severity="error">{parsed.error}</Alert>}
                        {bundle && (
                            <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
                                <Chip size="small" color="primary" label={describeAiBundle(bundle)} />
                                <Chip size="small" variant="outlined" label={`version ${bundle.version ?? '?'}`} />
                                {(bundle.exportedAt || bundle.exportedBy) && (
                                    <Typography variant="caption" color="text.secondary">
                                        Exported {formatAiDate(bundle.exportedAt)}{bundle.exportedBy ? ` by ${bundle.exportedBy}` : ''}
                                    </Typography>
                                )}
                            </Stack>
                        )}
                    </Stack>

                    <Divider />
                    <Stack gap={0.5}>
                        <Typography variant="subtitle1" fontWeight={600}>2. Mode</Typography>
                        <RadioGroup row value={mode} onChange={e => { setMode(e.target.value as AiImportMode); setResult(null); }}>
                            <FormControlLabel value="UPSERT" disabled={busy != null || applied} control={<Radio size="small" />}
                                label="Create new and update existing (UPSERT)" />
                            <FormControlLabel value="CREATE_ONLY" disabled={busy != null || applied} control={<Radio size="small" />}
                                label="Only create new (CREATE_ONLY)" />
                        </RadioGroup>
                        <Typography variant="caption" color="text.secondary">
                            {mode === 'UPSERT'
                                ? 'Existing tasks and agents are overwritten with the file. An agent\'s MCP servers, subagents and tasks are replaced by the links in the file.'
                                : 'Existing tasks and agents are left untouched (SKIP); only codes that do not exist yet are created.'}
                        </Typography>
                    </Stack>

                    <Divider />
                    <Stack gap={1.5}>
                        <Typography variant="subtitle1" fontWeight={600}>3. Preview & import</Typography>
                        <Typography variant="caption" color="text.secondary">
                            Preview runs a dry run: nothing is written. Import is all-or-nothing — with any error nothing is applied.
                        </Typography>
                        <AiErrorAlert error={error} onClose={() => setError(null)} />
                        {stalePreview && <Alert severity="info">The bundle or mode changed since the preview — preview again before importing.</Alert>}
                        {result && (result.applied
                            ? <Alert severity="success">
                                Imported: {result.summary.created} created, {result.summary.updated} updated, {result.summary.unchanged} unchanged
                                {result.summary.skipped > 0 ? `, ${result.summary.skipped} skipped` : ''}.
                            </Alert>
                            : <Alert severity="error">Nothing was imported — fix the errors below and preview again.</Alert>)}
                        {!result && previewCurrent && preview && preview.summary.errors > 0 && (
                            <Alert severity="warning">The preview has {preview.summary.errors} error{preview.summary.errors === 1 ? '' : 's'} — fix the bundle (or the target environment) and preview again.</Alert>
                        )}
                        {!result && previewCurrent && preview && preview.summary.errors === 0 && changes === 0 && (
                            <Alert severity="info">Nothing to change — every item is unchanged or skipped.</Alert>
                        )}
                        {report && <ImportSummary report={report} />}
                        {report && <ImportReportTable report={report} linkItems={applied} onNavigate={onClose} />}
                        {importedLinks.length > 0 && (
                            <Stack direction="row" gap={0.75} flexWrap="wrap" alignItems="center">
                                <Typography variant="body2" sx={{ mr: 0.5 }}>Open:</Typography>
                                {importedLinks.map(item => (
                                    <Chip key={`${item.type}:${item.code}`} size="small" clickable component={RouterLink} to={itemPath(item)}
                                        onClick={onClose} color={item.action === 'CREATE' ? 'success' : 'info'} variant="outlined"
                                        label={`${item.type === 'AGENT' ? 'agent' : 'task'} ${item.code}`} sx={{ fontFamily: 'monospace' }} />
                                ))}
                            </Stack>
                        )}
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Box sx={{ flex: 1 }} />
                <Button onClick={onClose} disabled={busy != null}>{applied ? 'Close' : 'Cancel'}</Button>
                {!applied && (
                    <>
                        <Button variant="outlined" disabled={bundle == null || busy != null}
                            startIcon={busy === 'preview' ? <CircularProgress size={16} /> : <VisibilityIcon />} onClick={runPreview}>
                            Preview
                        </Button>
                        <Button variant="contained" disabled={!canImport}
                            startIcon={busy === 'import' ? <CircularProgress size={16} color="inherit" /> : <FileUploadIcon />}
                            onClick={() => setConfirmOpen(true)}>
                            Import
                        </Button>
                    </>
                )}
            </DialogActions>
            <ConfirmDialog
                open={confirmOpen}
                title="Import bundle?"
                text={preview
                    ? <>
                        {preview.summary.created} item{preview.summary.created === 1 ? '' : 's'} will be created and {preview.summary.updated} updated
                        {mode === 'UPSERT' && preview.summary.updated > 0 ? '; updated agents get exactly the links in the file' : ''}.
                    </>
                    : ''}
                confirmLabel="Import"
                onClose={() => setConfirmOpen(false)}
                onConfirm={runImport}
            />
        </Dialog>
    );
}
